/**
 * TagPill Component
 *
 * Displays a tag as a clickable pill with an optional remove button.
 * Light green in light mode, dark green in dark mode.
 */

import React from 'react';

export interface TagPillProps {
  /** The tag ID */
  tagId: number;
  /** The tag name to display */
  tagName: string;
  /** Whether to show the remove (X) button */
  showRemove?: boolean;
  /** Called when the tag pill is clicked (for filtering) */
  onClick?: (tagId: number, tagName: string) => void;
  /** Called when the remove button is clicked */
  onRemove?: (tagId: number) => void;
  /** Whether the remove operation is in progress */
  isRemoving?: boolean;
}

export function TagPill({
  tagId,
  tagName,
  showRemove = false,
  onClick,
  onRemove,
  isRemoving = false,
}: TagPillProps) {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClick) {
      onClick(tagId, tagName);
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRemove && !isRemoving) {
      onRemove(tagId);
    }
  };

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium
        bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200
        ${onClick ? 'cursor-pointer hover:bg-green-200 dark:hover:bg-green-800/50 transition-colors' : ''}
        ${isRemoving ? 'opacity-50' : ''}`}
      onClick={onClick ? handleClick : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === 'Enter' && handleClick(e as unknown as React.MouseEvent) : undefined}
    >
      {tagName}
      {showRemove && (
        <button
          type="button"
          onClick={handleRemove}
          disabled={isRemoving}
          className="ml-0.5 inline-flex items-center justify-center rounded-full h-3.5 w-3.5
            text-green-600 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-700
            hover:text-green-800 dark:hover:text-green-100 transition-colors
            disabled:cursor-not-allowed"
          title={`Remove "${tagName}" tag`}
        >
          {isRemoving ? (
            <span className="h-2 w-2 animate-spin rounded-full border border-current border-t-transparent" />
          ) : (
            <svg
              className="h-2.5 w-2.5"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="3"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
        </button>
      )}
    </span>
  );
}

export default TagPill;
